// components/slides/SlidesView.tsx
'use client';

// Slides module entrypoint (mounted in the Portal main content area).
// activeProjectId === null → dashboard (import + deck cards); else the editor
// on the active deck. If a project is active but its deck is not in the store
// yet (e.g. the agent set it), we load it via the store's loadDeck.

import { useEffect } from 'react';
import { useSlidesStore } from './slidesStore';
import SlidesDashboard from './SlidesDashboard';
import SlidesEditor from './SlidesEditor';
import { useKernelStore } from '@/kernel/kernelStores';
import { SlidesWelcome } from './SlidesWelcome';
import { SlidesTour } from './SlidesTour';

const SlidesView = () => {
  const userId = useKernelStore((state) => state.userContext.dbUserId);
  const activeProjectId = useSlidesStore((s) => s.activeProjectId);
  const deck = useSlidesStore((s) => (activeProjectId ? s.decks[activeProjectId] : undefined));
  const loadDeck = useSlidesStore((s) => s.loadDeck);
  const setActiveProject = useSlidesStore((s) => s.setActiveProject);
  const pendingFocus = useSlidesStore((s) => s.pendingFocus);

  useEffect(() => {
    if (!activeProjectId || deck) return;
    void loadDeck(activeProjectId).then((loaded) => {
      if (loaded) return;
      // Unknown deck: fall back to the dashboard instead of a dead loader, and drop a deep
      // link aimed at it, or Portal would put the deck back and the load would run forever.
      const store = useSlidesStore.getState();
      if (store.pendingFocus?.projectId === activeProjectId) store.setPendingFocus(null);
      if (store.activeProjectId === activeProjectId) setActiveProject(null);
    });
  }, [activeProjectId, deck, loadDeck, setActiveProject]);

  // Loading a deck lands on its first page, so a deep link's page and element are applied
  // only once the deck is actually there, then forgotten.
  useEffect(() => {
    if (!pendingFocus || !deck || pendingFocus.projectId !== activeProjectId) return;
    const store = useSlidesStore.getState();
    const page = pendingFocus.pageId && deck.doc.pages.find((p) => p.id === pendingFocus.pageId);
    if (page) store.focusPage(deck.projectId, page.id);
    const element = pendingFocus.elementId && page && page.elements.some((e) => e.id === pendingFocus.elementId) ? pendingFocus.elementId : null;
    if (element) store.patchRuntime(deck.projectId, { selectedElementIds: [element], activeTool: 'select', rightBarTab: 'params' });
    store.setPendingFocus(null);
  }, [pendingFocus, deck, activeProjectId]);

  return <>
    {!activeProjectId ? <SlidesDashboard /> : deck ? <SlidesEditor deck={deck} /> : (
      <div className="flex h-full w-full items-center justify-center bg-black">
        <div className="flex items-center gap-2 text-xs text-white/40">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/40" />
          Opening deck…
        </div>
      </div>
    )}
    <SlidesWelcome key={userId ?? 'signed-out'} />
    <SlidesTour />
  </>;
};

export default SlidesView;
